import type { PileTypeAverage } from '@obraplan/core/application/use-cases/takeoff/rate-average-use-cases';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import { measureUnit } from '@obraplan/core/domain/takeoff/applies-to';
import {
  pileRateKey,
  type PileItemRate,
  type PileRateSnapshot,
  type PileSteel,
  type PileType,
} from '@obraplan/core/domain/takeoff/pile';
import { serviceLockKey, serviceLockKeyOf } from '@obraplan/core/domain/takeoff/project-budget';
import {
  referenceItemFor,
  type RateAverageItem,
  type ReferenceItem,
} from '@obraplan/core/domain/takeoff/rate-average';
import {
  itemDescription,
  type CatalogItem,
  type CatalogService,
  type ServiceCatalog,
} from '@obraplan/core/domain/takeoff/service-catalog';
import {
  STEEL_GAUGES,
  steelGaugeLabel,
  type SteelGauge,
} from '@obraplan/core/domain/takeoff/steel';
import { paraTexto } from './formato';
import type { WorkspaceAction } from './typology-draft';

/**
 * A média do diâmetro trazida para o tipo de estaca (Fase 39, etapa 2 — RN-505 a RN-507).
 *
 * ```text
 * cadastrar o R40  →  getPileTypeAverage(Ø 0,40)  →  trazerAMedia
 *                       ├─ ADD_SERVICE_ITEM dos itens da referência que a obra não tem
 *                       ├─ rates: o fator de cada item, MEDIA com a foto (o digitado fica)
 *                       └─ steel: o kg por bitola, MEDIA com a foto (o digitado fica)
 * ```
 *
 * Função pura sobre o catálogo da obra e a resposta da média: a tela só despacha e grava. O
 * fator aponta para o item **do catálogo da obra** (a chave do serviço com o sufixo da versão,
 * se houver, e a do item), porque é por essa chave que a conta acha o fator do tipo
 * (`calculatedByPileType`). O item que a obra ainda não tem ganha a chave que o rascunho vai dar
 * a ele ao acrescentá-lo — a mesma normalização do `ADD_SERVICE_ITEM`.
 */

export type AcrescentarItem = Extract<WorkspaceAction, { readonly type: 'ADD_SERVICE_ITEM' }>;

/** O que o tipo recebe, e o que a tela avisa. */
export interface MediaTrazida {
  /** Os itens da referência que faltam nos serviços de estaca da obra (RN-505). */
  readonly acoes: readonly AcrescentarItem[];
  /** A composição do tipo: MEDIA com a foto, e o digitado que já havia (RN-506). */
  readonly rates: readonly PileItemRate[];
  /** O aço por bitola, na ordem das bitolas. */
  readonly steel: readonly PileSteel[];
  readonly avisos: readonly string[];
}

/** O que alguém já escreveu e a média não apaga (D9). */
export interface JaDigitado {
  /** O aço digitado no formulário: fica DIGITADO, com a média ao lado. */
  readonly aco?: readonly Pick<PileSteel, 'gauge' | 'kgPerPile'>[];
  /**
   * A composição que o tipo já tinha, quando ele muda de diâmetro. Só o DIGITADO fica: o que
   * seguia a média era a de outro diâmetro, e nunca vale para este (D6).
   */
  readonly fatores?: readonly PileItemRate[];
}

/**
 * O que o orçamento aprovado segura (Fase 33, RN-509): no serviço aprovado a composição não muda
 * — nem item novo, nem fator —, e com o aço da estaca aprovado o kg por bitola também não.
 */
export interface Travas {
  /** O serviço desta chave do catálogo está aprovado? */
  readonly servico?: (serviceKey: string) => boolean;
  /** Algum serviço que mede o aço da estaca está aprovado. */
  readonly aco?: boolean;
}

const SEM_TRAVA = (): boolean => false;

/** O fator que a média dá a um item do catálogo da obra. */
export interface FatorDaMedia {
  /** A chave do serviço **no catálogo da obra**, com o sufixo da versão, se houver. */
  readonly serviceKey: string;
  readonly itemKey: string;
  /** Como a obra escreve o item; o da referência, quando ele ainda vai ser acrescentado. */
  readonly descricao: string;
  readonly rate: number;
  readonly average: PileRateSnapshot;
  /** "M³/m³", "KG/kg" — a unidade do item sobre a medida do serviço. */
  readonly unidade: string;
}

/** Um número que seguia a média e que a média de hoje mudou (RN-507). */
export interface MudancaDaMedia {
  /** `pileRateKey` do item, ou `aco:<bitola>`. */
  readonly chave: string;
  readonly descricao: string;
  readonly de: number;
  readonly para: number;
  readonly unidade: string;
  /** O dia da foto que mudou, `aaaa-mm-dd` — "a média mudou desde 23/10". */
  readonly desde?: string;
}

// ── Os arredondamentos do banco ─────────────────────────────────────────────────

/**
 * As casas que a coluna guarda (`0088`): o fator e a foto dele em seis, o kg da bitola em três
 * (`checkPileTypeFields`) e a foto do aço em quatro. Arredondar aqui faz a tela comparar o que
 * o banco devolve, e não uma sobra de ponto flutuante que acenderia "a média mudou" à toa.
 */
const casas = (valor: number, n: number): number => Math.round(valor * 10 ** n) / 10 ** n;
const seis = (valor: number): number => casas(valor, 6);
const quatro = (valor: number): number => casas(valor, 4);
const tres = (valor: number): number => casas(valor, 3);

/** Duas médias são a mesma até a quarta casa — a que a tela mostra (RN-507). */
export function mesmaMedia(a: number, b: number): boolean {
  return Math.round(a * 10_000) === Math.round(b * 10_000);
}

// ── O catálogo da obra ──────────────────────────────────────────────────────────

/** A chave que o `ADD_SERVICE_ITEM` dá ao item novo: a descrição em maiúsculas, normalizada. */
export function chaveDoItemNovo(description: string): string {
  return normalizeName(itemDescription(description));
}

/**
 * O serviço da obra que casa com o da média, pela chave da trava (`serviceLockKey`): a versão
 * escreve "CONCRETAGEM (V2)", e é o mesmo serviço da referência.
 */
export function servicoDaObra(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  chaveDaTrava: string,
): CatalogService | undefined {
  const servicos = (catalog?.disciplines ?? []).flatMap((disciplina) => disciplina.services);
  return (
    servicos.find((servico) => servico.key === chaveDaTrava) ??
    servicos.find((servico) => serviceLockKey(servico.key) === chaveDaTrava)
  );
}

export function comoItemDaMedia(item: CatalogItem): RateAverageItem {
  return {
    description: item.description,
    kind: item.kind,
    ...(item.code === undefined ? {} : { code: item.code }),
    ...(item.unit === undefined ? {} : { unit: item.unit }),
    ...(item.jobRoleId === undefined ? {} : { jobRoleId: item.jobRoleId }),
    ...(item.steelGauge === undefined ? {} : { steelGauge: item.steelGauge }),
  };
}

/**
 * O item da obra que é o da referência (RN-498, `referenceItemFor`).
 *
 * A identidade forte primeiro — o mesmo código, a mesma função —, para que a descrição parecida
 * de outro item não ganhe o fator. O aço casa também pela bitola: a obra que já tem o vergalhão
 * de 16 mm com outro nome não ganha outro, porque cada item da bitola leva o aço inteiro dela
 * (RN-378) e o aço sairia em dobro. Por último a chave que o item novo teria: com ela, o rascunho
 * não acrescentaria nada (o item já está lá), e o fator vai para o que está lá.
 */
export function itemDaObra(servico: CatalogService, ref: ReferenceItem): CatalogItem | undefined {
  const maoDeObra = ref.kind === 'MAO_DE_OBRA';
  return (
    servico.items.find((item) =>
      maoDeObra
        ? item.kind === 'MAO_DE_OBRA' &&
          ref.jobRoleId !== undefined &&
          item.jobRoleId === ref.jobRoleId
        : item.kind !== 'MAO_DE_OBRA' &&
          ref.code !== undefined &&
          item.code !== undefined &&
          normalizeName(item.code) === normalizeName(ref.code),
    ) ??
    (ref.steelGauge === undefined
      ? undefined
      : servico.items.find((item) => item.steelGauge === ref.steelGauge)) ??
    servico.items.find((item) => referenceItemFor(comoItemDaMedia(item), [ref]) !== undefined) ??
    servico.items.find((item) => item.key === chaveDoItemNovo(ref.description))
  );
}

/** "M³/m³"; sem a unidade do item, "por m³". */
function unidadeDoFator(unidadeDoItem: string | undefined, medida: string): string {
  const unidade = unidadeDoItem?.trim() ?? '';
  return unidade === '' ? `por ${medida}` : `${unidade}/${medida}`;
}

/** A unidade do fator de um item do catálogo, pela medida do serviço. */
export function unidadeDoItemNoServico(
  item: Pick<CatalogItem, 'unit'>,
  servico: Pick<CatalogService, 'appliesTo' | 'measureBy'>,
): string {
  return unidadeDoFator(item.unit, measureUnit(servico.appliesTo, servico.measureBy));
}

// ── A média, no catálogo da obra ────────────────────────────────────────────────

/**
 * O fator que a média dá a cada item, pela `pileRateKey` do catálogo da obra.
 *
 * Só o que não é aço: o item com bitola é kg **por estaca**, e vai para o aço do tipo
 * (`acoDaMedia`). O serviço que a obra não tem fica fora — não há onde o fator morar.
 */
export function fatoresDaMedia(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  media: Pick<PileTypeAverage, 'at' | 'averages'>,
): ReadonlyMap<string, FatorDaMedia> {
  const fatores = new Map<string, FatorDaMedia>();
  for (const average of media.averages) {
    if (average.item.steelGauge !== undefined) continue;
    const servico = servicoDaObra(catalog, average.serviceKey);
    if (servico === undefined) continue;
    const rate = seis(average.rate);
    if (!(rate > 0)) continue;
    const daObra = itemDaObra(servico, average.item);
    const itemKey = daObra?.key ?? chaveDoItemNovo(average.item.description);
    const chave = pileRateKey(servico.key, itemKey);
    if (fatores.has(chave)) continue;
    fatores.set(chave, {
      serviceKey: servico.key,
      itemKey,
      descricao: daObra?.description ?? itemDescription(average.item.description),
      rate,
      average: { rate, at: media.at, projects: average.projects, piles: average.pieces },
      unidade: unidadeDoFator(average.item.unit, average.measureUnit),
    });
  }
  return fatores;
}

/**
 * O kg por estaca de cada bitola (RN-501), com a foto. Duas linhas da mesma bitola (dois
 * códigos do mesmo vergalhão na referência) somam: a estaca leva as duas.
 */
export function acoDaMedia(
  media: Pick<PileTypeAverage, 'at' | 'averages'>,
): ReadonlyMap<SteelGauge, PileRateSnapshot> {
  const somas = new Map<SteelGauge, { rate: number; projects: number; piles: number }>();
  for (const average of media.averages) {
    const bitola = average.item.steelGauge;
    if (bitola === undefined) continue;
    const antes = somas.get(bitola);
    somas.set(bitola, {
      rate: (antes?.rate ?? 0) + average.rate,
      projects: Math.max(antes?.projects ?? 0, average.projects),
      piles: Math.max(antes?.piles ?? 0, average.pieces),
    });
  }
  const aco = new Map<SteelGauge, PileRateSnapshot>();
  for (const [bitola, soma] of somas) {
    // O kg vai com três casas ao tipo e a foto com quatro: o que arredonda a zero não é aço.
    if (!(tres(soma.rate) > 0)) continue;
    aco.set(bitola, {
      rate: quatro(soma.rate),
      at: media.at,
      projects: soma.projects,
      piles: soma.piles,
    });
  }
  return aco;
}

// ── Cadastrar o tipo, ou mudar o diâmetro (RN-505) ──────────────────────────────

/** "40", "42,5" — o diâmetro em centímetros, como o projeto de fundação o escreve. */
function centimetros(diameter: number): string {
  return paraTexto(Math.round(diameter * 1000) / 10);
}

const SEM_REFERENCIA =
  'Sem obra de referência, não há média: o tipo fica com o que foi digitado. A referência se ' +
  'escolhe em Configurações → Serviços → Taxas médias.';

/** O fator digitado, sem a foto de outro diâmetro. */
function semFoto(fator: PileItemRate): PileItemRate {
  return {
    serviceKey: fator.serviceKey,
    itemKey: fator.itemKey,
    rate: fator.rate,
    origin: 'DIGITADO',
  };
}

/**
 * O que o tipo recebe da média do diâmetro, e os itens que a obra ganha (RN-505).
 *
 * - O item da referência que falta no serviço da obra vira `ADD_SERVICE_ITEM`: nunca se apaga
 *   nem se renomeia nada (D3). O serviço que a obra não tem fica de fora, com o aviso.
 * - Cada item com média ganha o fator MEDIA, com a foto (D8). O digitado fica, com a média nova
 *   ao lado (D9); o digitado sem média neste diâmetro fica sem foto.
 * - O aço da média vai por bitola, em kg por estaca. O kg digitado no formulário fica DIGITADO,
 *   com a média ao lado.
 * - Sem estaca do diâmetro nas obras da média, o fator fica vazio e a tela avisa (D6). Os itens
 *   da referência entram assim mesmo: o cadastro do serviço não depende do diâmetro.
 * - O serviço aprovado não ganha item nem fator, e o que o tipo já tinha nele fica como estava
 *   (RN-509).
 */
export function trazerAMedia(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  media: PileTypeAverage,
  digitado: JaDigitado = {},
  travas: Travas = {},
): MediaTrazida {
  const travado = travas.servico ?? SEM_TRAVA;
  const avisos: string[] = [];
  if (media.reference === undefined) avisos.push(SEM_REFERENCIA);
  else if (media.pieces === 0) {
    avisos.push(
      `Nenhuma estaca Ø ${centimetros(media.diameter)} cm nas obras da média: o tipo fica sem fator.`,
    );
  }

  // Os itens que faltam, serviço a serviço, na ordem do cadastro da referência.
  const acoes: AcrescentarItem[] = [];
  const planejadas = new Set<string>();
  const ausentes = new Map<string, string>();
  const aprovadosComFalta = new Map<string, string>();
  const anotarAusente = (disciplina: string, servico: string): void => {
    const chave = serviceLockKeyOf(disciplina, servico);
    if (!ausentes.has(chave)) ausentes.set(chave, `${disciplina} › ${servico}`);
  };
  for (const ref of media.referenceItems) {
    const servico = servicoDaObra(catalog, serviceLockKeyOf(ref.disciplineName, ref.serviceName));
    if (servico === undefined) {
      anotarAusente(ref.disciplineName, ref.serviceName);
      continue;
    }
    if (itemDaObra(servico, ref) !== undefined) continue;
    if (travado(servico.key)) {
      aprovadosComFalta.set(servico.key, `${ref.disciplineName} › ${ref.serviceName}`);
      continue;
    }
    const chave = pileRateKey(servico.key, chaveDoItemNovo(ref.description));
    if (planejadas.has(chave)) continue;
    planejadas.add(chave);
    acoes.push({
      type: 'ADD_SERVICE_ITEM',
      serviceKey: servico.key,
      description: ref.description,
      // A classe da referência é decisão de gente, e não palpite: vai como está lá.
      kind: ref.kind,
      ...(ref.code === undefined ? {} : { code: ref.code }),
      ...(ref.unit === undefined ? {} : { unit: ref.unit }),
      ...(ref.kind === 'MAO_DE_OBRA' && ref.jobRoleId !== undefined
        ? { jobRoleId: ref.jobRoleId }
        : {}),
      // O vergalhão leva a bitola da referência: a descrição pode não escrevê-la.
      ...(ref.steelGauge === undefined ? {} : { steelGauge: ref.steelGauge }),
    });
  }
  for (const average of media.averages) {
    if (servicoDaObra(catalog, average.serviceKey) === undefined) {
      anotarAusente(average.disciplineName, average.serviceName);
    }
  }
  for (const nome of ausentes.values()) {
    avisos.push(
      `A obra não tem o serviço ${nome} da obra de referência: os itens dele ficam sem fator.`,
    );
  }
  for (const nome of aprovadosComFalta.values()) {
    avisos.push(
      `O serviço ${nome} está aprovado no orçamento: os itens da referência que faltam nele não entram.`,
    );
  }

  // A composição: a média onde há, e o digitado que já havia. O serviço aprovado fica como estava.
  const anteriores = digitado.fatores ?? [];
  const digitados = new Map(
    anteriores
      .filter((fator) => fator.origin === 'DIGITADO' && !travado(fator.serviceKey))
      .map((fator) => [pileRateKey(fator.serviceKey, fator.itemKey), fator]),
  );
  const rates: PileItemRate[] = anteriores.filter((fator) => travado(fator.serviceKey));
  for (const [chave, fator] of fatoresDaMedia(catalog, media)) {
    if (travado(fator.serviceKey)) continue;
    const meu = digitados.get(chave);
    if (meu !== undefined) {
      rates.push({ ...semFoto(meu), average: fator.average });
      digitados.delete(chave);
      continue;
    }
    rates.push({
      serviceKey: fator.serviceKey,
      itemKey: fator.itemKey,
      rate: fator.rate,
      origin: 'MEDIA',
      average: fator.average,
    });
  }
  for (const resto of digitados.values()) rates.push(semFoto(resto));

  // O aço: o digitado fica, a média preenche as outras bitolas.
  const aco = acoDaMedia(media);
  const acoDigitado = new Map((digitado.aco ?? []).map((linha) => [linha.gauge, linha.kgPerPile]));
  const steel = STEEL_GAUGES.flatMap((info): PileSteel[] => {
    const foto = aco.get(info.code);
    const kg = acoDigitado.get(info.code);
    if (kg !== undefined) {
      return [
        {
          gauge: info.code,
          kgPerPile: kg,
          origin: 'DIGITADO',
          ...(foto === undefined ? {} : { average: foto }),
        },
      ];
    }
    if (foto === undefined) return [];
    return [{ gauge: info.code, kgPerPile: tres(foto.rate), origin: 'MEDIA', average: foto }];
  });

  return { acoes, rates, steel, avisos };
}

// ── A média mudou (RN-507) ──────────────────────────────────────────────────────

/**
 * O que segue a média e a média de hoje mudou, na quarta casa (RN-507). O digitado não entra:
 * ele não muda com a média. O item que a média de hoje não tem também não — a foto continua
 * valendo, e trocar por "sem fator" calaria o item.
 */
export function mudancasDaMedia(
  tipo: Pick<PileType, 'rates' | 'steel'>,
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  media: Pick<PileTypeAverage, 'at' | 'averages'>,
  travas: Travas = {},
): readonly MudancaDaMedia[] {
  const travado = travas.servico ?? SEM_TRAVA;
  const daMedia = fatoresDaMedia(catalog, media);
  const mudancas: MudancaDaMedia[] = [];
  for (const linha of tipo.rates ?? []) {
    if (linha.origin !== 'MEDIA' || travado(linha.serviceKey)) continue;
    const chave = pileRateKey(linha.serviceKey, linha.itemKey);
    const novo = daMedia.get(chave);
    const de = linha.average?.rate ?? linha.rate;
    if (novo === undefined || mesmaMedia(de, novo.rate)) continue;
    mudancas.push({
      chave,
      descricao: novo.descricao,
      de,
      para: novo.rate,
      unidade: novo.unidade,
      ...(linha.average === undefined ? {} : { desde: linha.average.at }),
    });
  }
  const aco = travas.aco === true ? new Map<SteelGauge, PileRateSnapshot>() : acoDaMedia(media);
  for (const linha of tipo.steel) {
    if (linha.origin !== 'MEDIA') continue;
    const novo = aco.get(linha.gauge);
    const de = linha.average?.rate ?? linha.kgPerPile;
    if (novo === undefined || mesmaMedia(de, novo.rate)) continue;
    mudancas.push({
      chave: `aco:${linha.gauge}`,
      descricao: steelGaugeLabel(linha.gauge),
      de,
      para: novo.rate,
      unidade: 'KG/estaca',
      ...(linha.average === undefined ? {} : { desde: linha.average.at }),
    });
  }
  return mudancas;
}

/**
 * "Atualizar pela média" (RN-507): troca o fator e o kg que seguem a média; o digitado fica,
 * com a média nova ao lado (D9). O item que o tipo não tem não entra — quem o tirou, tirou. O
 * serviço aprovado é pulado, e o aço também, quando o dele está aprovado (RN-509).
 */
export function atualizarPelaMedia(
  tipo: Pick<PileType, 'rates' | 'steel'>,
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  media: Pick<PileTypeAverage, 'at' | 'averages'>,
  travas: Travas = {},
): { readonly rates: readonly PileItemRate[]; readonly steel: readonly PileSteel[] } {
  const travado = travas.servico ?? SEM_TRAVA;
  const daMedia = fatoresDaMedia(catalog, media);
  const aco = travas.aco === true ? new Map<SteelGauge, PileRateSnapshot>() : acoDaMedia(media);
  return {
    rates: (tipo.rates ?? []).map((linha): PileItemRate => {
      if (travado(linha.serviceKey)) return linha;
      const novo = daMedia.get(pileRateKey(linha.serviceKey, linha.itemKey));
      if (novo === undefined) return linha;
      return linha.origin === 'MEDIA'
        ? { ...linha, rate: novo.rate, average: novo.average }
        : { ...linha, average: novo.average };
    }),
    steel: tipo.steel.map((linha): PileSteel => {
      const novo = aco.get(linha.gauge);
      if (novo === undefined) return linha;
      return linha.origin === 'MEDIA'
        ? { ...linha, kgPerPile: tres(novo.rate), average: novo }
        : { ...linha, origin: 'DIGITADO', average: novo };
    }),
  };
}

// ── A edição na composição (RN-506) ─────────────────────────────────────────────

/**
 * O fator escrito à mão: vira DIGITADO, sem motivo (D9), com a média ao lado. Vazio ou zero tira
 * o fator — o item volta ao índice da obra (RN-508). O mesmo número não muda nada: quem clicou e
 * saiu do campo não digitou.
 */
export function digitarFator(
  rates: readonly PileItemRate[],
  serviceKey: string,
  itemKey: string,
  valor: number | undefined,
  media: PileRateSnapshot | undefined,
): readonly PileItemRate[] {
  const chave = pileRateKey(serviceKey, itemKey);
  const atual = rates.find((linha) => pileRateKey(linha.serviceKey, linha.itemKey) === chave);
  // Zero é "sem fator" (RN-281), como o vazio: o item sai da composição.
  const rate = valor === undefined ? 0 : seis(valor);
  if (!(rate > 0)) {
    return atual === undefined ? rates : rates.filter((linha) => linha !== atual);
  }
  if (atual !== undefined && atual.rate === rate) return rates;
  const foto = media ?? atual?.average;
  const novo: PileItemRate = {
    serviceKey,
    itemKey,
    rate,
    origin: 'DIGITADO',
    ...(foto === undefined ? {} : { average: foto }),
  };
  return atual === undefined
    ? [...rates, novo]
    : rates.map((linha) => (linha === atual ? novo : linha));
}

/** "Usar a média" num item: volta a seguir a média, com a foto (RN-506). */
export function usarAMediaNoFator(
  rates: readonly PileItemRate[],
  fator: Pick<FatorDaMedia, 'serviceKey' | 'itemKey' | 'average'>,
): readonly PileItemRate[] {
  const chave = pileRateKey(fator.serviceKey, fator.itemKey);
  const novo: PileItemRate = {
    serviceKey: fator.serviceKey,
    itemKey: fator.itemKey,
    rate: fator.average.rate,
    origin: 'MEDIA',
    average: fator.average,
  };
  return rates.some((linha) => pileRateKey(linha.serviceKey, linha.itemKey) === chave)
    ? rates.map((linha) => (pileRateKey(linha.serviceKey, linha.itemKey) === chave ? novo : linha))
    : [...rates, novo];
}

/** O kg escrito à mão numa bitola: DIGITADO, com a média ao lado. Vazio ou zero tira a bitola. */
export function digitarAco(
  steel: readonly PileSteel[],
  gauge: SteelGauge,
  kg: number | undefined,
  media: PileRateSnapshot | undefined,
): readonly PileSteel[] {
  const atual = steel.find((linha) => linha.gauge === gauge);
  const kgPerPile = kg === undefined ? 0 : tres(kg);
  if (!(kgPerPile > 0)) return steel.filter((linha) => linha.gauge !== gauge);
  if (atual !== undefined && atual.kgPerPile === kgPerPile) return steel;
  const foto = media ?? atual?.average;
  const novo: PileSteel = {
    gauge,
    kgPerPile,
    origin: 'DIGITADO',
    ...(foto === undefined ? {} : { average: foto }),
  };
  return emOrdemDeBitola(
    atual === undefined ? [...steel, novo] : steel.map((linha) => (linha === atual ? novo : linha)),
  );
}

/** "Usar a média" numa bitola. */
export function usarAMediaNoAco(
  steel: readonly PileSteel[],
  gauge: SteelGauge,
  media: PileRateSnapshot,
): readonly PileSteel[] {
  const novo: PileSteel = { gauge, kgPerPile: tres(media.rate), origin: 'MEDIA', average: media };
  return emOrdemDeBitola([...steel.filter((linha) => linha.gauge !== gauge), novo]);
}

function emOrdemDeBitola(steel: readonly PileSteel[]): readonly PileSteel[] {
  const ordem = new Map(STEEL_GAUGES.map((info, indice) => [info.code, indice]));
  return [...steel].sort((a, b) => (ordem.get(a.gauge) ?? 99) - (ordem.get(b.gauge) ?? 99));
}
